// How well he knows you. EARNED, never bought: it's simply how many of the
// things you left him he decided to keep. No token, no holdings, no wallet.
//
// Deliberately not framed as a rank to climb. It's a memory of you, so the
// names say what he'd think if he saw you again rather than where you sit in
// a hierarchy. The mechanic underneath is unchanged.
//
// Shared by the shrine page (display) and /api/role (resolution) so the
// thresholds live in exactly one place.
//
// Colours are checked against the cream panel (#f7e7d2), not picked by eye:
// the old set was chosen for a black background and the pale yellow measured
// under 1.5:1 once the panel turned cream.

// How long the climb is, in kept offerings.
//
// Measured 2026-08-19, which is why these moved: at 3 submits a day and a
// 59% acceptance rate, somebody gains about 1.8 kept a day. On the old
// ladder (1 / 3 / 10) that made "a familiar face" an AFTERNOON, and one
// person reached 7 kept inside 1.2 days while another hit 3 in a single
// sitting with no days elapsed at all. That rung is the one that opens the
// poster maker and putting events forward, so it was handing out the keys
// to the town on day one.
//
// Now: a few days before he starts recognising you, a fortnight of coming
// back before he trusts you with a wall, a month and a half before you can
// put things on the calendar yourself. Grinding it out IS the mechanic,
// and it can only be done by returning, because the daily allowance caps
// the burst.
//
// Env-tunable so this can be dialled without a deploy while we watch how
// it actually feels.
const n = (v, dflt) => Number(v) || dflt
const KNOWS = n(process.env.NEXT_PUBLIC_TIER_KNOWS, 3)
const FAMILIAR = n(process.env.NEXT_PUBLIC_TIER_FAMILIAR, 20)
const OWN = n(process.env.NEXT_PUBLIC_TIER_OWN, 60)

// Who gets post from him.
//
// This used to sit at KNOWS, the first rung, which made a letter from him
// the thing that happened on your third kept offering. Writing to somebody
// is the most personal surface in the whole product and it should be the
// one people climb toward, so it starts where he actually knows your face.
// One door: the sim's candidate query and the viewer's locked state both
// read this, or the inbox promises something the cafe never delivers.
export const MAIL_MIN_KEPT = n(process.env.NEXT_PUBLIC_TIER_MAIL, FAMILIAR)

export const TIERS = [
  {
    name: 'One of his own',
    min: OWN,
    color: '#8a5a12',
    card: '/images/ranks/kiwi_tier_annointed.webp',
    subtitle: 'he would know you anywhere',
    // What the rung earns in the world. Kept ON the ladder so a threshold
    // move (they are env-tunable above) carries its powers with it, and no
    // route ever writes 60-means-ten again.
    actionsPerDay: 10,
    influence: 12,
  },
  {
    name: 'A familiar face',
    min: FAMILIAR,
    color: '#5f42ab',
    card: '/images/ranks/kiwi_tier_prophet.webp',
    subtitle: 'he keeps going back to the things you leave',
    actionsPerDay: 7,
    influence: 8,
  },
  {
    name: 'Someone he knows',
    min: KNOWS,
    color: '#175f83',
    card: '/images/ranks/kiwi_tier_faithful.webp',
    subtitle: 'he has read something of yours and kept it',
    actionsPerDay: 5,
    influence: 6,
  },
  {
    name: 'A new face',
    min: 0,
    color: '#57506e',
    card: '/images/ranks/kiwi_tier_congregation.webp',
    subtitle: 'he has not met you yet',
    actionsPerDay: 3,
    influence: 4,
  },
]

/** Resolve a kept-offering count to its tier (highest threshold met). */
export function resolveTier(acceptedCount = 0) {
  return TIERS.find((t) => acceptedCount >= t.min) || TIERS[TIERS.length - 1]
}

/** The tier above the given one, or null if he already knows you best. */
export function nextTier(currentName) {
  const i = TIERS.findIndex((t) => t.name === currentName)
  return i > 0 ? TIERS[i - 1] : null
}

// ── What standing actually buys ─────────────────────────────────────────
//
// The first real gates. Standing was pure display until 2026-08 — nothing
// anywhere read the tier. These keep the earned-never-bought rule: both
// perks come from him keeping your offerings, not from holdings.

// ── What standing lets you do to the town's calendar ────────────────────
//
// The other gates are allowances (how much of him you get). These are
// responsibilities: how much of the world you get to touch. Watch, then
// vote, then put something forward, then put something up yourself.
//
// `built` is load-bearing and honest. The rungs arrived before the flows
// behind them, and showing a power as live when pressing it does nothing
// is worse than showing it as coming.
//
// The vote threshold is duplicated as VOTE_MIN_ACCEPTED in lib/ballots.js,
// which is what the API actually enforces. Move both.

export const CALENDAR_POWERS = [
  {
    id: 'watch',
    min: 0,
    label: 'see what is coming',
    detail: 'the same noticeboard he reads',
    built: true,
  },
  {
    id: 'vote',
    min: 1,
    // The drawer replaces this line with one that points at the open
    // ballot, because "where do I actually vote" is the first thing
    // anyone asks. This is the fallback for when nothing is open.
    label: 'vote on what the town puts on',
    detail: 'the winning event goes up on the calendar and he finds it on a poster',
    built: true,
  },
  {
    id: 'propose',
    // Same rung as "a familiar face": the tier names and the powers must
    // not drift apart, or the card says one thing and the door does another.
    min: FAMILIAR,
    label: 'put an event forward',
    detail: 'Your event joins the next thing the town votes on',
    built: false,
  },
  {
    id: 'schedule',
    min: OWN,
    label: 'put an event on the calendar yourself',
    detail: 'no vote, it just happens, and he has to live with it',
    built: false,
  },
]

/** The civic ladder, marked up with what this many kept offerings reaches. */
export function calendarPowers(accepted = 0) {
  return CALENDAR_POWERS.map((p) => ({ ...p, unlocked: accepted >= p.min }))
}

/**
 * How many posters you may keep.
 *
 * A cap on the count is a cap on the storage: a saved poster is one image
 * at the size the world hangs it, and deleting one deletes the file. Low
 * enough to stay a scrapbook rather than a hosting account.
 */
export function postersAllowed(tierName) {
  switch (tierName) {
    case 'One of his own':   return 15
    case 'A familiar face':  return 6
    default:                 return 0
  }
}

/** Offerings per day. He gives more of his attention to people he knows. */
// (a new face one a day, from 31 Oct: about 850 came in a day after a post of his went round, 440 different people in 500,
// and he could not keep up. Sam: "we can reduce the number of messages from new people to one a day")
// (and the rest about halved, 1 Nov: about 1,100 a day came and his sorter put 300 in front of him, most of them from
// people he knows. it was 3, 4 and 5. Sam chose "roughly halve it")
// (and eased a rung at a time, 2 Nov: people said they were held at fewer than the page told them, and the flow had
// died down a little. Sam: "lets go 1,2,3,4 on notes allowed and see how the flow is")
export function submitsPerDay(tierName) {
  switch (tierName) {
    case 'One of his own':   return 4
    case 'A familiar face':  return 3
    case 'Someone he knows': return 2
    default:                 return 1
  }
}

// ── What a kept offering does to him ────────────────────────────────────
//
// The one power that is not an allowance. A kept offering writes its words
// into his interest profile, and `influence` above is HOW MANY times, so a
// gift from somebody he knows well pulls three times as hard as a
// stranger's on what he reads about, which films he picks, what he takes
// up, and what his drawings look like.
//
// THE SIM READS THIS TOO (world/internet/InterestProfile.js). It lives
// here because this file imports nothing and is therefore safe to share
// across both halves, same as lib/world/placeActions.js. It used to be a
// second table over there and the two drifted the moment the ladder was
// renamed: every offering silently landed at a stranger's weight for
// weeks. One table now, and the aliases below keep the sim's own older
// vocabulary resolving.
const INFLUENCE_ALIASES = {
  congregation: 'A new face',
  faithful: 'Someone he knows',
  prophets: 'A familiar face',
  anointed: 'One of his own',
}

/** How hard this rung's offerings pull. Unknown names read as a stranger. */
export function influenceFor(tierName) {
  const raw = String(tierName || '').trim().toLowerCase()
  const name = INFLUENCE_ALIASES[raw] || tierName
  const tier = TIERS.find((t) => t.name.toLowerCase() === String(name || '').trim().toLowerCase())
  return tier?.influence ?? TIERS[TIERS.length - 1].influence
}

// A letter pulls, but not like a gift.
//
// Writing to him is a second way in, and it should be: a real exchange
// ought to leave a mark. But an offering is a thing you chose and he
// chose to keep, judged, one per day; a letter is a reply in a
// conversation and there can be several. Left at parity, mail would
// quietly become the cheaper lever and the shrine would stop mattering.
//
// A quarter, floored at one, derived from the same table so the two can
// never drift apart: a familiar face's letter pulls 2 where their
// offering pulls 8.
const MAIL_INFLUENCE_SHARE = 4

export function mailInfluenceFor(tierName) {
  return Math.max(1, Math.round(influenceFor(tierName) / MAIL_INFLUENCE_SHARE))
}

/** The same, as a multiple of what a stranger's gift is worth. */
export function influenceMultiple(tierName) {
  const base = TIERS[TIERS.length - 1].influence
  return influenceFor(tierName) / base
}
